// src/routes/domains/australia/australia.routes.tsx

import { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import { createProtectedRoute } from '../../core/createProtectedRoute';
import type { AppRoute } from '../../core/types';

// ------------------------------
// Public marketing hub (eager, if exists)
// ------------------------------
// import AustraliaImmigrationHub from '@/pages/marketing/AustraliaImmigrationHub';

// ------------------------------
// Lazy‑loaded Australia components (absolute imports)
// ------------------------------

// AustraliaImmigrationFlightDeck, AustraliaJourneyMap, and
// AustraliaSavedJourneyMaps were retired 2026-08-15: they read from a
// pre-UPRM localStorage journey system that every real Australia assessment
// has already moved off of (all 11 route straight to /assessment-results,
// zero localStorage writes) — same situation as Germany's retired Flight
// Deck. The file's own prior developer comment already noted "Flight Deck
// predates UPRM... no longer sits in the live navigation path." Archived to
// archive/australia-legacy-dashboard/ — all three routes below now redirect
// to the real, UPRM-wired /journey-compass.
const AustraliaToolsEcosystem = lazy(() => import('@/pages/AustraliaToolsEcosystem'));
const AustraliaEvisaGateway = lazy(() => import('@/pages/AustraliaEvisaGateway'));
const AustraliaVisaFormConductor = lazy(() => import('@/pages/AustraliaVisaFormConductor'));
const AustraliaReunificationAccelerator = lazy(() => import('@/pages/AustraliaReunificationAccelerator'));

// Assessments
const AustraliaWorkingHolidayAssessment = lazy(() => import('@/pages/AustraliaWorkingHolidayAssessment'));
const AustraliaStudentAssessment = lazy(() => import('@/pages/AustraliaStudentAssessment'));
const AustraliaEmployerSponsoredAssessment = lazy(() => import('@/pages/AustraliaEmployerSponsoredAssessment')); // 482
const AustraliaSkilledIndependentAssessment = lazy(() => import('@/pages/AustraliaSkilledIndependentAssessment')); // 189
const AustraliaAssessmentResultAnalysis = lazy(() => import('@/pages/AustraliaAssessmentResultAnalysis'));

// Additional Australia‑specific tools (if any)
// const AustraliaMaintenanceFundsCalculator = lazy(() => import('@/pages/AustraliaMaintenanceFundsCalculator'));

// ------------------------------
// Australia Routes Definition
// ------------------------------
export const australiaRoutes: AppRoute[] = [
  // Optional public marketing hub (uncomment if needed)
  // {
  //   id: 'australia.marketing_hub',
  //   domain: 'australia',
  //   path: '/australia-immigration-hub',
  //   element: createProtectedRoute(<AustraliaImmigrationHub />, {
  //     protected: false,
  //     meta: { title: 'Australia Immigration Hub', region: 'AU', intent: 'guide' },
  //   }),
  // },

  // Core tools & dashboards
  {
    id: 'australia.tools_ecosystem',
    domain: 'australia',
    path: '/au-tools-ecosystem',
    element: createProtectedRoute(<AustraliaToolsEcosystem />, {
      tier: 'premium',
      meta: {
        title: 'Australia Tools Ecosystem',
        region: 'AU',
        intent: 'dashboard',
        aiTags: ['australia', 'ecosystem', 'tools'],
      },
    }),
  },
  // Retired — see the archival note by the imports above.
  { id: 'australia.flight_deck', domain: 'australia', path: '/australia-immigration-flight-deck', element: <Navigate to="/journey-compass" replace /> },
  {
    id: 'australia.evisa_gateway',
    domain: 'australia',
    path: '/australia-evisa-gateway',
    element: createProtectedRoute(<AustraliaEvisaGateway />, {
      tier: 'premium',
      meta: {
        title: 'Australia eVisa Gateway',
        region: 'AU',
        intent: 'gateway',
        aiTags: ['evisa', 'gateway', 'australia'],
      },
    }),
  },
  {
    id: 'australia.visa_form',
    domain: 'australia',
    path: '/australia-visa-form',
    element: createProtectedRoute(<AustraliaVisaFormConductor />, {
      tier: 'premium',
      meta: {
        title: 'Australia Visa Form Conductor',
        region: 'AU',
        intent: 'form',
        aiTags: ['visa', 'form', 'application'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // AustraliaToolsEcosystem.tsx catalog id 'visa-form'.
        toolId: 'au-visa-form',
        category: 'documents',
      },
    }),
  },
  // Retired — see the archival note by the imports above.
  { id: 'australia.journey_map', domain: 'australia', path: '/australia-journey-map', element: <Navigate to="/journey-compass" replace /> },
  // Retired — see the archival note by the imports above.
  { id: 'australia.saved_journey_maps', domain: 'australia', path: '/australia-saved-journey-maps', element: <Navigate to="/journey-compass" replace /> },
  {
    id: 'australia.reunification_accelerator',
    domain: 'australia',
    path: '/australia-reunification-accelerator',
    element: createProtectedRoute(<AustraliaReunificationAccelerator />, {
      tier: 'premium',
      meta: {
        title: 'Australia Reunification Accelerator',
        region: 'AU',
        intent: 'tool',
        aiTags: ['reunification', 'family', 'accelerator'],
      },
    }),
  },
  {
    // Retired 2026-08-29 — unified into /interview-simulator (all countries, one shared
    // trainer). The old dedicated component also fabricated a per-nationality "risk level"
    // with no real source; archived rather than kept alive. See PROJECT_HISTORY.md.
    id: 'australia.interview_simulator',
    domain: 'australia',
    path: '/australia-interview-simulator',
    element: <Navigate to="/interview-simulator" replace />,
  },

  // Assessments
  {
    id: 'australia.working_holiday',
    domain: 'australia',
    path: '/australia-working-holiday-assessment',
    element: createProtectedRoute(<AustraliaWorkingHolidayAssessment />, {
      tier: 'premium',
      meta: {
        title: 'Australia Working Holiday Assessment',
        region: 'AU',
        intent: 'assessment',
        aiTags: ['working holiday', 'visa', 'assessment'],
      },
    }),
  },
  {
    id: 'australia.student',
    domain: 'australia',
    path: '/australia-student-assessment',
    element: createProtectedRoute(<AustraliaStudentAssessment />, {
      tier: 'premium',
      meta: {
        title: 'Australia Student Visa Assessment',
        region: 'AU',
        intent: 'assessment',
        aiTags: ['student', 'visa', 'assessment'],
      },
    }),
  },
  {
    id: 'australia.employer_sponsored_482',
    domain: 'australia',
    path: '/australia-employer-sponsored-assessment',
    element: createProtectedRoute(<AustraliaEmployerSponsoredAssessment />, {
      tier: 'premium',
      meta: {
        title: 'Australia Employer Sponsored (482) Assessment',
        region: 'AU',
        intent: 'assessment',
        aiTags: ['482', 'employer sponsored', 'assessment'],
        // Tool-usage tracking (see docs on RouteMeta.toolId) — matches the
        // AustraliaToolsEcosystem.tsx catalog id 'employer-sponsored'.
        toolId: 'au-employer-sponsored',
        category: 'assessments',
      },
    }),
  },
  {
    id: 'australia.skilled_independent_189',
    domain: 'australia',
    path: '/australia-skilled-independent-assessment',
    element: createProtectedRoute(<AustraliaSkilledIndependentAssessment />, {
      tier: 'premium',
      meta: {
        title: 'Australia Skilled Independent (189) Assessment',
        region: 'AU',
        intent: 'assessment',
        aiTags: ['189', 'skilled independent', 'assessment'],
        // Tool-usage tracking pilot (see docs on RouteMeta.toolId) — matches
        // the AustraliaToolsEcosystem.tsx catalog id 'skilled-independent-assessment'.
        toolId: 'au-skilled-independent-assessment',
        category: 'assessments',
      },
    }),
  },
  {
    id: 'australia.assessment_result',
    domain: 'australia',
    path: '/australia-assessment-result-analysis',
    element: createProtectedRoute(<AustraliaAssessmentResultAnalysis />, {
      tier: 'premium',
      meta: {
        title: 'Australia Assessment Result Analysis',
        region: 'AU',
        intent: 'analysis',
        aiTags: ['assessment', 'result', 'analysis'],
      },
    }),
  },
];